import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Typography,
    FormLabel,
    Input,
    Chip,
    OutlinedInput,
    Checkbox,
    ListItemText,
    ToggleButtonGroup,
    ToggleButton,
    Divider,
} from '@mui/material';
import * as CONSTANTS from "../../constants";
import { PlaceTagNumbersMapping } from "../../mappings/placeCategories";

const PlaceFilter = ({ resetFilter, submitFilter, lang }) => {
    const [sortBy, setSortBy] = useState('name');
    const [placeName, setPlaceName] = useState('');
    const [location, setLocation] = useState('');
    const [category, setCategory] = useState('');
    const [includeTags, setIncludeTags] = useState([]);
    const [excludeTags, setExcludeTags] = useState([]);
    const [quickFilter, setQuickFilter] = useState('');

    // Place categories from constants
    const placeCategories = Object.keys(CONSTANTS.PLACE_CATEGORIES).map(key => ({
        value: key,
        label: CONSTANTS.PLACE_CATEGORIES[key][lang]
    }));

    // Available tags from constants with numbers - using PlaceTagNumbersMapping for correct order
    const availableTags = Object.entries(PlaceTagNumbersMapping).map(([number, tagKey]) => ({
        value: tagKey,
        label: CONSTANTS.PLACE_TAGS[tagKey][lang],
        number: parseInt(number)
    }));

    // Helper function to get tag label from value
    const getTagLabel = (tagValue) => {
        const tag = availableTags.find(tag => tag.value === tagValue);
        return tag ? tag.label : tagValue;
    };

    // Helper function to get tag number from value
    const getTagNumber = (tagValue) => {
        const tag = availableTags.find(tag => tag.value === tagValue);
        return tag ? tag.number : null;
    };

    // Helper function to get tag value from number
    const getTagValue = (tagNumber) => {
        const tag = availableTags.find(tag => tag.number === tagNumber);
        return tag ? tag.value : null;
    };

    // Quick filter options
    const quickFilterOptions = [
        { value: 'wotagei_playing', label: CONSTANTS.PLACE_FILTER.WOTAGEI_PLAYING[lang] },
        { value: 'dining', label: CONSTANTS.PLACE_FILTER.DINING[lang] },
        { value: 'entertainment', label: CONSTANTS.PLACE_FILTER.ENTERTAINMENT[lang] },
        { value: 'outdoor', label: CONSTANTS.PLACE_FILTER.OUTDOOR[lang] },
    ];

    const handleQuickFilterChange = (event, newFilter) => {
        setQuickFilter(newFilter);
        
        // Apply quick filter logic using tag numbers
        if (newFilter === 'wotagei_playing') {
            setCategory('');
            setIncludeTags([21, 22]); // WOTAGEI_FRIENDLY, SPACIOUS
            setExcludeTags([]);
        } else if (newFilter === 'dining') {
            setCategory('');
            setIncludeTags([4, 5]); // FOOD, DRINKS
            setExcludeTags([]);
        } else if (newFilter === 'entertainment') {
            setCategory('ENTERTAINMENT');
            setIncludeTags([]);
            setExcludeTags([]);
        } else if (newFilter === 'outdoor') {
            setCategory('');
            setIncludeTags([6]); // OUTDOOR
            setExcludeTags([]);
        }
    };

    const handleSubmit = () => {
        const filter = new URLSearchParams();
        filter.set('page', 1);
        filter.set('sort_by', sortBy);
        
        if (placeName) filter.set('place_name', placeName);
        if (location) filter.set('location', location);
        if (category) filter.set('category', category);
        if (includeTags.length > 0) filter.set('include_tags', includeTags.join(','));
        if (excludeTags.length > 0) filter.set('exclude_tags', excludeTags.join(','));
        
        submitFilter(filter);
    };

    const handleReset = () => {
        setSortBy('name');
        setPlaceName('');
        setLocation('');
        setCategory('');
        setIncludeTags([]);
        setExcludeTags([]);
        setQuickFilter('');
        resetFilter();
    };

    const handleIncludeTagsChange = (event) => {
        const value = event.target.value;
        setIncludeTags(typeof value === 'string' ? value.split(',') : value);
    };

    const handleExcludeTagsChange = (event) => {
        const value = event.target.value;
        setExcludeTags(typeof value === 'string' ? value.split(',') : value);
    };

    return (
        <Box>
            {/* Quick Filter Buttons */}
            <Box mb={3}>
                <FormLabel sx={{ typography: 'title-md', fontWeight: 'bold', mb: 1 }}>
                    {CONSTANTS.PLACE_FILTER.QUICK_FILTERS[lang]}
                </FormLabel>
                <ToggleButtonGroup
                    value={quickFilter}
                    exclusive
                    onChange={handleQuickFilterChange}
                    aria-label="quick filters"
                    size="small"
                    fullWidth
                >
                    {quickFilterOptions.map((option) => (
                        <ToggleButton key={option.value} value={option.value}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Sorting Options */}
            <Box mb={3}>
                <FormLabel sx={{ typography: 'title-md', fontWeight: 'bold' }}>
                    {CONSTANTS.PLACE_FILTER.SORTING_OPTION[lang]}
                </FormLabel>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel>{CONSTANTS.PLACE_FILTER.SORTING_OPTION[lang]}</InputLabel>
                    <Select
                        value={sortBy}
                        label={CONSTANTS.PLACE_FILTER.SORTING_OPTION[lang]}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <MenuItem value="name">{CONSTANTS.PLACE_SORT_OPTIONS.NAME[lang]}</MenuItem>
                        <MenuItem value="location">{CONSTANTS.PLACE_SORT_OPTIONS.LOCATION[lang]}</MenuItem>
                        <MenuItem value="rating">{CONSTANTS.PLACE_SORT_OPTIONS.RATING[lang]}</MenuItem>
                        <MenuItem value="views">{CONSTANTS.PLACE_SORT_OPTIONS.VIEWS[lang]}</MenuItem>
                        <MenuItem value="date_created">{CONSTANTS.PLACE_SORT_OPTIONS.DATE_CREATED[lang]}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Searching Options */}
            <Box mb={3}>
                <FormLabel sx={{ typography: 'title-md', fontWeight: 'bold' }}>
                    {CONSTANTS.PLACE_FILTER.SEARCHING_OPTION[lang]}
                </FormLabel>
                
                {/* Place Name */}
                <FormLabel sx={{ typography: 'title-md', fontWeight: 'bold', mb: 1, mt: 2 }}>
                    {CONSTANTS.PLACE_FILTER.PLACE_NAME[lang]}
                </FormLabel>
                <TextField
                    fullWidth
                    size="small"
                    value={placeName}
                    onChange={(e) => setPlaceName(e.target.value)}
                    placeholder={CONSTANTS.PLACE_FILTER.PLACE_NAME_PLACEHOLDER[lang]}
                    sx={{ mb: 2 }}
                />

                {/* Location */}
                <FormLabel sx={{ typography: 'title-md', fontWeight: 'bold', mb: 1 }}>
                    {CONSTANTS.PLACE_FILTER.LOCATION[lang]}
                </FormLabel>
                <TextField
                    fullWidth
                    size="small"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder={CONSTANTS.PLACE_FILTER.LOCATION_PLACEHOLDER[lang]}
                    sx={{ mb: 2 }}
                />

                {/* Category */}
                <FormLabel sx={{ typography: 'title-md', fontWeight: 'bold', mb: 1 }}>
                    {CONSTANTS.PLACE_FILTER.CATEGORY[lang]}
                </FormLabel>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>{CONSTANTS.PLACE_FILTER.CATEGORY[lang]}</InputLabel>
                    <Select
                        value={category}
                        label={CONSTANTS.PLACE_FILTER.CATEGORY[lang]}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        <MenuItem value="">{CONSTANTS.PLACE_FILTER.ALL_CATEGORIES[lang]}</MenuItem>
                        {placeCategories.map((cat) => (
                            <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Include Tags */}
                <Typography level="title-md" fontWeight="bold" sx={{ mb: 2 }}>
                    {CONSTANTS.PLACE_FILTER.TAGS[lang]}
                </Typography>
                <InputLabel>{CONSTANTS.PLACE_FILTER.INCLUDE[lang]}</InputLabel>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Select
                        multiple
                        value={includeTags}
                        onChange={handleIncludeTagsChange}
                        input={<OutlinedInput label={CONSTANTS.PLACE_FILTER.INCLUDE[lang]} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value} label={getTagLabel(getTagValue(value))} size="small" />
                                ))}
                            </Box>
                        )}
                    >
                        {availableTags.map((tag) => (
                            <MenuItem key={tag.value} value={tag.number}>
                                <Checkbox checked={includeTags.indexOf(tag.number) > -1} />
                                <ListItemText primary={tag.label} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Exclude Tags */}
                <InputLabel sx={{mt: 2}}>{CONSTANTS.PLACE_FILTER.EXCLUDE[lang]}</InputLabel>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Select
                        multiple
                        value={excludeTags}
                        onChange={handleExcludeTagsChange}
                        input={<OutlinedInput label={CONSTANTS.PLACE_FILTER.EXCLUDE[lang]} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value} label={getTagLabel(getTagValue(value))} size="small" />
                                ))}
                            </Box>
                        )}
                    >
                        {availableTags.map((tag) => (
                            <MenuItem key={tag.value} value={tag.number}>
                                <Checkbox checked={excludeTags.indexOf(tag.number) > -1} />
                                <ListItemText primary={tag.label} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Action Buttons */}
            <Box display="flex" gap={2}>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    fullWidth
                >
                    {CONSTANTS.PLACE_FILTER.RESET[lang]}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    fullWidth
                >
                    {CONSTANTS.PLACE_FILTER.SEARCH[lang]}
                </Button>
            </Box>
        </Box>
    );
};

export default PlaceFilter; 